import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'

export type CensorInput = (input: string) => string | Promise<string>

type VirtualGiftItem = {
  value: string[]
  url?: string
}

type VirtualGiftSectionContextProps = {
  id: string
  step: number
  setStep: (step: number) => void
  quantity: number
  setQuantity: (quantity: number) => void
  items: VirtualGiftItem[]
  updateItemValue: (index: number, value: string, indexValue: number) => void
  updateItemUrl: (index: number, url: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  settings: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateSettings: (settings: any) => void
  censorInput?: CensorInput
}

const VirtualGiftSectionContext = createContext<VirtualGiftSectionContextProps | undefined>(undefined)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const VirtualGiftSectionProvider: React.FC<{
  children: ReactNode
  settings: any
  censorInput?: CensorInput
}> = ({ children, ...props }) => {
  const [step, setStep] = useState(0)
  const [quantity, setQuantity] = useState(props.settings?.styles?.options?.defaultQuantity || 1)
  const [items, setItems] = useState<VirtualGiftItem[]>([])
  const [settings, setSettings] = useState(props.settings)

  const updateItemValue = (index: number, value: string, indexValue: number) => {
    setItems((prevItems) => {
      const newItems = [...prevItems]
      if (!newItems[index]) {
        newItems[index] = { value: [] }
      }
      const newValues = [...newItems[index].value]
      newValues[indexValue] = value
      newItems[index] = { value: newValues }
      return newItems
    })
  }

  const updateItemUrl = (index: number, url: string) => {
    setItems((prevItems) => {
      const newItems = [...prevItems]
      if (!newItems[index]) {
        newItems[index] = { value: [], url: '' }
      }
      newItems[index].url = url
      return newItems
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateSettings = (newSettings: any) => {
    setSettings(newSettings)
  }

  useEffect(() => {
    setItems(Array.from({ length: quantity }, () => ({ value: [] })))
  }, [quantity])

  return (
    <VirtualGiftSectionContext.Provider
      value={{
        id: props.settings.id,
        step,
        quantity,
        items,
        settings,
        updateSettings,
        updateItemValue,
        updateItemUrl,
        setQuantity,
        setStep,
        censorInput: props.censorInput,
      }}
    >
      {children}
    </VirtualGiftSectionContext.Provider>
  )
}

export const VirtualGiftSectionConsumer = VirtualGiftSectionContext.Consumer

export const useVirtualGiftSection = (): VirtualGiftSectionContextProps => {
  const context = useContext(VirtualGiftSectionContext)
  if (!context) {
    throw new Error('useVirtualGiftSection must be used within a VirtualGiftSectionProvider')
  }
  return context
}
